import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { feedbackAPI } from '../../lib/api'
import { motion, AnimatePresence } from 'framer-motion'
import { AlertCircle, CheckCircle, Clock, User, Calendar, Send } from 'lucide-react'
import { format } from 'date-fns'
import { de } from 'date-fns/locale'
import toast from 'react-hot-toast'

export function KnowledgeGaps() {
  const [selectedGap, setSelectedGap] = useState<any>(null)
  const [response, setResponse] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const queryClient = useQueryClient()

  const { data: gaps, isLoading } = useQuery(
    ['knowledge-gaps', statusFilter],
    () => feedbackAPI.getKnowledgeGaps(statusFilter === 'all' ? undefined : statusFilter)
  )

  const resolveMutation = useMutation(
    (params: { gapId: string; response: string }) => 
      feedbackAPI.resolveKnowledgeGap(params.gapId, params.response),
    {
      onSuccess: () => {
        toast.success('Wissenslücke beantwortet')
        setSelectedGap(null)
        setResponse('')
        queryClient.invalidateQueries('knowledge-gaps')
      },
      onError: () => {
        toast.error('Fehler beim Speichern der Antwort')
      }
    }
  )

  const handleResolve = () => {
    if (selectedGap && response.trim()) {
      resolveMutation.mutate({
        gapId: selectedGap.id,
        response: response.trim()
      })
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open':
        return <AlertCircle className="w-4 h-4 text-yellow-500" />
      case 'in_progress':
        return <Clock className="w-4 h-4 text-blue-500" />
      case 'resolved':
        return <CheckCircle className="w-4 h-4 text-green-500" />
      default:
        return null
    }
  }

  const getPriorityColor = (priority: number) => {
    if (priority <= 2) return 'text-red-500'
    if (priority === 3) return 'text-yellow-500'
    return 'text-gray-500'
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Gaps List */}
      <div className="lg:col-span-2 space-y-4">
        {/* Filters */}
        <div className="flex space-x-2">
          {['all', 'open', 'in_progress', 'resolved'].map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-3 py-1.5 rounded-lg transition-smooth ${
                statusFilter === status
                  ? 'bg-primary-600 text-white'
                  : 'glass hover:bg-white/10'
              }`}
            >
              {status === 'all' ? 'Alle' :
               status === 'open' ? 'Offen' :
               status === 'in_progress' ? 'In Bearbeitung' : 'Gelöst'}
            </button>
          ))}
        </div>

        {/* Gaps */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="inline-block w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : gaps && gaps.length > 0 ? (
          <div className="space-y-3">
            {gaps.map((gap: any) => (
              <motion.div
                key={gap.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                onClick={() => setSelectedGap(gap)}
                className={`glass rounded-lg p-4 cursor-pointer transition-all hover:bg-white/10 ${
                  selectedGap?.id === gap.id ? 'ring-2 ring-primary-500' : ''
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(gap.status)}
                    <span className={`text-sm font-medium ${getPriorityColor(gap.priority)}`}>
                      Priorität {gap.priority}
                    </span>
                  </div>
                  <span className="text-xs text-gray-500">
                    {format(new Date(gap.created_at), 'dd.MM.yyyy HH:mm', { locale: de })}
                  </span>
                </div>
                
                <h3 className="font-medium mb-2">{gap.question}</h3>
                
                {gap.context && (
                  <p className="text-sm text-gray-400 mb-2">{gap.context}</p>
                )}
                
                {gap.status === 'resolved' && gap.response && (
                  <div className="mt-3 pt-3 border-t border-white/10">
                    <p className="text-sm text-green-400">Beantwortet:</p>
                    <p className="text-sm text-gray-300 mt-1">{gap.response}</p>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-gray-600" />
            <p className="text-gray-400">Keine Wissenslücken gefunden</p>
          </div>
        )}
      </div>

      {/* Response Panel */}
      <div className="lg:col-span-1">
        <AnimatePresence mode="wait">
          {selectedGap && selectedGap.status === 'open' ? (
            <motion.div
              key="response-form"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="glass rounded-xl p-6 sticky top-6"
            >
              <h3 className="font-bold mb-4">Antwort verfassen</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Frage:</label>
                  <p className="text-gray-300">{selectedGap.question}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Ihre Antwort:</label>
                  <textarea
                    value={response}
                    onChange={(e) => setResponse(e.target.value)}
                    placeholder="Antwort eingeben..."
                    rows={8}
                    className="input w-full"
                  />
                </div>
                
                <button
                  onClick={handleResolve}
                  disabled={resolveMutation.isLoading || !response.trim()}
                  className="w-full btn btn-primary flex items-center justify-center space-x-2"
                >
                  <Send className="w-4 h-4" />
                  <span>{resolveMutation.isLoading ? 'Wird gesendet...' : 'Antwort senden'}</span>
                </button>
              </div>
            </motion.div>
          ) : selectedGap ? (
            <motion.div
              key="gap-details"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="glass rounded-xl p-6 sticky top-6"
            >
              <h3 className="font-bold mb-4">Details</h3>
              
              <div className="space-y-3 text-sm">
                <div>
                  <span className="text-gray-500">Status:</span>
                  <div className="flex items-center space-x-2 mt-1">
                    {getStatusIcon(selectedGap.status)}
                    <span>{
                      selectedGap.status === 'open' ? 'Offen' :
                      selectedGap.status === 'in_progress' ? 'In Bearbeitung' : 'Gelöst'
                    }</span>
                  </div>
                </div>
                
                <div>
                  <span className="text-gray-500">Priorität:</span>
                  <p className={`font-medium ${getPriorityColor(selectedGap.priority)}`}>
                    {selectedGap.priority} / 5
                  </p>
                </div>
                
                <div>
                  <span className="text-gray-500">Erstellt:</span>
                  <p>{format(new Date(selectedGap.created_at), 'dd.MM.yyyy HH:mm', { locale: de })}</p>
                </div>
                
                {selectedGap.resolved_at && (
                  <div>
                    <span className="text-gray-500">Beantwortet:</span>
                    <p>{format(new Date(selectedGap.resolved_at), 'dd.MM.yyyy HH:mm', { locale: de })}</p>
                  </div>
                )}
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="glass rounded-xl p-6 text-center"
            >
              <AlertCircle className="w-12 h-12 mx-auto mb-4 text-gray-600" />
              <p className="text-gray-400">
                Wählen Sie eine Wissenslücke aus, um Details zu sehen
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}